// src/components/cmmc/CMMCDashboard.js
// Standalone CMMC Compliance Tracker - NO dependency on RPA system
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
 
const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';
const SOCKET_URL = (() => {
  try { const u = new URL(API_URL); return `${u.protocol}//${u.host}`; }
  catch { return API_URL.replace(/\/api\/?$/, ''); }
})();
 
// ============================================================================
// CONSTANTS
// ============================================================================
const FAMILIES = {
  AC: { name: 'Access Control', count: 22, color: '#3B82F6' },
  AT: { name: 'Awareness & Training', count: 3, color: '#8B5CF6' },
  AU: { name: 'Audit & Accountability', count: 9, color: '#06B6D4' },
  CM: { name: 'Configuration Mgmt', count: 9, color: '#10B981' },
  IA: { name: 'ID & Authentication', count: 11, color: '#F59E0B' },
  IR: { name: 'Incident Response', count: 3, color: '#EF4444' },
  MA: { name: 'Maintenance', count: 6, color: '#EC4899' },
  MP: { name: 'Media Protection', count: 9, color: '#14B8A6' },
  PE: { name: 'Physical Protection', count: 6, color: '#6366F1' },
  PS: { name: 'Personnel Security', count: 2, color: '#F97316' },
  RA: { name: 'Risk Assessment', count: 3, color: '#84CC16' },
  CA: { name: 'Security Assessment', count: 4, color: '#A855F7' },
  SC: { name: 'System & Comms', count: 16, color: '#0EA5E9' },
  SI: { name: 'System & Info Integrity', count: 7, color: '#F43F5E' }
};
 
const PHASES = [
  { id: 'phase0', name: 'Discovery & Scoping', icon: '🔍', color: '#06B6D4' },
  { id: 'phase1', name: 'Gap Assessment', icon: '📊', color: '#F59E0B' },
  { id: 'phase2', name: 'Remediation', icon: '🔧', color: '#F97316' },
  { id: 'phase3', name: 'Documentation', icon: '📝', color: '#10B981' },
  { id: 'phase4', name: 'Self-Assessment', icon: '✅', color: '#3B82F6' },
  { id: 'phase5', name: 'Maintain & Audit Prep', icon: '🛡️', color: '#8B5CF6' }
];
 
const STATUS_COLORS = {
  met: '#10B981', not_met: '#EF4444', not_applicable: '#6B7280', not_assessed: '#475569',
  completed: '#10B981', in_progress: '#F59E0B', not_started: '#475569', blocked: '#EF4444'
};
 
const STATUS_LABELS = {
  met: 'MET', not_met: 'NOT MET', not_applicable: 'N/A', not_assessed: 'Not Assessed',
  completed: 'Done', in_progress: 'In Progress', not_started: 'Not Started', blocked: 'Blocked'
};
 
const fmtDate = (d) => { if (!d) return '—'; const dt = new Date(d); return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(); };
 
// Reserved question IDs use hand-tuned classification; all other questions
// (AI-generated or coverage-gap fallback) use generic AI classification
// driven by their own controlsAffected array — both paths auto-update
// requirements, so this is now used only to label HOW a question maps,
// not WHETHER it maps.
const RESERVED_QUESTION_IDS = {
  q1_m365: true, q2_ssp: true, q3_mfa: true, q4_cui_employees: false,
  q5_msp: true, q6_antivirus: true, q7_training: true, q8_incident_response: true,
  q9_cage_uei: false, q10_affirming_official: false
};
 
// ============================================================================
// STYLES (dark theme matching TJG brand)
// ============================================================================
const S = {
  page: { fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", background: '#0B1120', minHeight: '100vh', color: '#CBD5E1', padding: '20px 20px 60px' },
  container: { maxWidth: 1240, margin: '0 auto' },
  header: { background: 'linear-gradient(135deg, #111827 0%, #1E293B 100%)', borderRadius: 12, padding: '20px 24px', marginBottom: 20, border: '1px solid #1E293B' },
  card: { background: '#111827', borderRadius: 10, border: '1px solid #1E293B', padding: 16, marginBottom: 12 },
  statCard: { background: '#111827', borderRadius: 10, border: '1px solid #1E293B', padding: 16, flex: '1 1 170px', minWidth: 150 },
  tabs: { display: 'flex', gap: 2, marginBottom: 20, background: '#111827', borderRadius: 10, padding: 4, border: '1px solid #1E293B', flexWrap: 'wrap' },
  tab: (active) => ({ padding: '10px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 12, background: active ? '#3B82F6' : 'transparent', color: active ? '#FFF' : '#64748B', transition: 'all 0.15s', whiteSpace: 'nowrap' }),
  btn: (color = '#3B82F6') => ({ padding: '8px 16px', background: color, color: '#FFF', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }),
  btnSm: (color = '#3B82F6') => ({ padding: '5px 10px', background: color, color: '#FFF', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: 11 }),
  select: { padding: '6px 10px', background: '#1E293B', color: '#E2E8F0', border: '1px solid #334155', borderRadius: 6, fontSize: 13 },
  input: { padding: '8px 12px', background: '#1E293B', color: '#E2E8F0', border: '1px solid #334155', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  textarea: { padding: '8px 12px', background: '#1E293B', color: '#E2E8F0', border: '1px solid #334155', borderRadius: 6, fontSize: 13, width: '100%', minHeight: 60, resize: 'vertical', boxSizing: 'border-box' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: `${color}20`, color, border: `1px solid ${color}40` }),
  label: { fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  value: { fontSize: 22, fontWeight: 800, color: '#F8FAFC' },
  grid: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  famBtn: (active, color) => ({ padding: '4px 10px', background: active ? `${color}20` : 'transparent', color: active ? color : '#64748B', border: `1px solid ${active ? color : '#1E293B'}`, borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 })
};
 
const ProgressBar = ({ pct, color = '#3B82F6', h = 8 }) => (
  <div style={{ position: 'relative', height: h, background: '#1E293B', borderRadius: h / 2, overflow: 'hidden', width: '100%' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: h / 2, transition: 'width 0.4s ease' }} />
  </div>
);
 
// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CMMCDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [trackerId, setTrackerId] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeFamily, setActiveFamily] = useState('all');
  const [expandedReqs, setExpandedReqs] = useState(new Set());
  const [savingReq, setSavingReq] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionDrafts, setQuestionDrafts] = useState({});
  const [processingQuestion, setProcessingQuestion] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [generationSummary, setGenerationSummary] = useState(null);
  const [calculatingScore, setCalculatingScore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [importingRequirements, setImportingRequirements] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [answerResults, setAnswerResults] = useState({});
  const [importingSteps, setImportingSteps] = useState(false);
  const [stepsImportResult, setStepsImportResult] = useState(null);
  const stepsFileRef = useRef(null); // questionId -> { derivedCount, classificationMode }
  const [initForm, setInitForm] = useState({ orgName: 'TJG', orgFullName: 'The Joachim Group CPAs & Consultants, LLC', cageCode: '', uei: '', targetLevel: 'level2_self' });
  const socketRef = useRef(null);
  const requirementsFileRef = useRef(null);
 
  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchTrackers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/cmmc/trackers`, { withCredentials: true });
      if (res.data.ok && res.data.trackers?.length > 0) {
        const t = res.data.trackers[0];
        setTrackerId(t._id);
        await fetchTracker(t._id);
      } else {
        setTracker(null);
        setTrackerId(null);
      }
    } catch (err) {
      console.error('[CMMC] fetchTrackers error:', err);
      if (err.response?.status !== 404) setError('Failed to load CMMC trackers');
      setTracker(null);
    } finally {
      setLoading(false);
    }
  }, []);
 
  const fetchTracker = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/admin/cmmc/${id}`, { withCredentials: true });
      if (res.data.ok && res.data.tracker) {
        setTracker(res.data.tracker);
        setError(null);
      }
    } catch (err) {
      console.error('[CMMC] fetchTracker error:', err);
      setError(err.response?.data?.error || 'Failed to load tracker');
    }
  }, []);
 
  // ============================================================================
  // SOCKET.IO
  // ============================================================================
  useEffect(() => {
    const s = io(SOCKET_URL, { transports: ['websocket', 'polling'], path: '/socket.io', withCredentials: true });
    socketRef.current = s;
    s.on('connect', () => console.log('[CMMC] Socket connected:', s.id));
    s.on('rpa_workflow_update', (evt) => {
      if (!evt.type?.startsWith('cmmc_')) return;
      const id = trackerId || tracker?._id;
      if (!id) return;

      if (evt.type === 'cmmc_requirement_updated' && evt.reqId && evt.newStatus) {
        setTracker(prev => {
          if (!prev) return prev;
          const reqs = [...prev.requirements];
          const idx  = reqs.findIndex(r => r.reqId === evt.reqId);
          if (idx !== -1) {
            reqs[idx] = {
              ...reqs[idx],
              status: evt.newStatus,
              notes:  evt.note || reqs[idx].notes,
              lastAssessedAt: new Date().toISOString()
            };
          }
          return { ...prev, requirements: reqs };
        });
      } else if (evt.type === 'cmmc_question_answered') {
        if (evt.progressScore !== undefined) {
          setScoreResult(prev => prev ? {
            ...prev,
            progressScore:       evt.progressScore,
            progressMaxPossible: evt.progressMaxPossible,
            score:               evt.submissionScore,
          } : null);
        }
        fetchTracker(id);
      } else {
        fetchTracker(id);
      }
    });
    return () => s.disconnect();
  }, [trackerId, tracker, fetchTracker]);
 
  useEffect(() => { fetchTrackers(); }, [fetchTrackers]);
 
  // ============================================================================
  // ACTIONS
  // ============================================================================
  const initializeTracker = async () => {
    try {
      setError(null);
      const res = await axios.post(`${API_URL}/admin/cmmc/initialize`, { ...initForm, includeDhra: true }, { withCredentials: true });
      if (res.data.ok) {
        setTracker(res.data.tracker);
        setTrackerId(res.data.tracker._id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize tracker');
    }
  };
 
  const updateRequirement = async (reqId, updates) => {
    if (!trackerId) return;
    try {
      setSavingReq(reqId);
      const res = await axios.patch(`${API_URL}/admin/cmmc/${trackerId}/requirements/${reqId}`, updates, { withCredentials: true });
      if (res.data.ok) {
        setTracker(prev => {
          if (!prev) return prev;
          const reqs = [...prev.requirements];
          const idx = reqs.findIndex(r => r.reqId === reqId);
          if (idx !== -1) reqs[idx] = res.data.requirement;
          return { ...prev, requirements: reqs };
        });
      }
    } catch (err) { console.error('[CMMC] updateRequirement error:', err); }
    finally { setSavingReq(null); }
  };
 
  const updateStep = async (stepId, updates) => {
    if (!trackerId) return;
    try {
      await axios.patch(`${API_URL}/admin/cmmc/${trackerId}/steps/${stepId}`, updates, { withCredentials: true });
      fetchTracker(trackerId);
    } catch (err) { console.error('[CMMC] updateStep error:', err); }
  };
 
  const calculateScore = async (idOverride) => {
    const raw = idOverride || trackerId || tracker?._id;
    if (!raw) return;
    const id = typeof raw === 'string' ? raw : raw?.toString();
    if (!id || id === '[object Object]') return;
    try {
      setCalculatingScore(true);
      const res = await axios.post(`${API_URL}/admin/cmmc/${id}/calculate-score`, { save: true }, { withCredentials: true });
      if (res.data.ok) { setScoreResult(res.data); fetchTracker(id); }
    } catch (err) { console.error('[CMMC] calculateScore error:', err); }
    finally { setCalculatingScore(false); }
  };
 
  const answerQuestion = async (questionId, answer) => {
    const id = trackerId || tracker?._id;
    if (!id) return;
    try {
      setProcessingQuestion(questionId);
      setEditingQuestion(null);
      setQuestionDrafts(prev => { const next = { ...prev }; delete next[questionId]; return next; });
      const res = await axios.patch(`${API_URL}/admin/cmmc/${id}/questions/${questionId}`, { answer }, { withCredentials: true });
      if (res.data.ok) {
        setAnswerResults(prev => ({
          ...prev,
          [questionId]: {
            derivedCount: res.data.derivedUpdates?.length || 0,
            classificationMode: res.data.classificationMode
          }
        }));
        await fetchTracker(id);
        await calculateScore(id);
      }
    } catch (err) { console.error('[CMMC] answerQuestion error:', err); }
    finally { setProcessingQuestion(null); }
  };
 
  const generateQuestions = async () => {
    const id = trackerId || tracker?._id;
    if (!id) return;
    try {
      setGeneratingQuestions(true);
      setGenerationSummary(null);
      const res = await axios.post(`${API_URL}/admin/cmmc/${id}/generate-questions`, {}, { withCredentials: true });
      if (res.data.ok) {
        setGenerationSummary({ summary: res.data.summary, count: res.data.count, preserved: res.data.preserved, coverage: res.data.coverage });
        await fetchTracker(id);
      }
    } catch (err) { console.error('[CMMC] generateQuestions error:', err); }
    finally { setGeneratingQuestions(false); }
  };

  const pinQuestion = async (questionId, pinned) => {
    const id = trackerId || tracker?._id;
    if (!id) return;
    try {
      setTracker(prev => {
        if (!prev) return prev;
        const qs = prev.leadershipQuestions.map(q =>
          q.id === questionId ? { ...q, pinned } : q
        );
        return { ...prev, leadershipQuestions: qs };
      });
      await axios.patch(`${API_URL}/admin/cmmc/${id}/questions/${questionId}/pin`, { pinned }, { withCredentials: true });
    } catch (err) {
      console.error('[CMMC] pinQuestion error:', err);
      await fetchTracker(id);
    }
  };

  const exportQuestionsCSV = () => {
    if (!questions.length) return;
    const headers = ['ID', 'Question', 'Why / Controls Affected', 'Impact', 'Answered', 'Answer', 'Answered By', 'Answered At', 'Pinned'];
    const rows = questions.map(q => [
      q.id || '',
      `"${(q.question || '').replace(/"/g, '""')}"`,
      `"${(q.why || '').replace(/"/g, '""')}"`,
      q.impact || '',
      q.answered ? 'Yes' : 'No',
      `"${(q.answer || '').replace(/"/g, '""')}"`,
      q.answeredBy || '',
      q.answeredAt ? new Date(q.answeredAt).toLocaleDateString() : '',
      q.pinned ? 'Yes' : 'No',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `TJG_CMMC_Open_Items_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportRequirementsCSV = async () => {
    const id = trackerId || tracker?._id;
    if (!id) return;
    try {
      const res = await fetch(`${API_URL}/admin/cmmc/${id}/requirements/export`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `TJG_CMMC_Requirements_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[CMMC] exportRequirements error:', err);
    }
  };

  const importRequirementsCSV = async (file) => {
    const id = trackerId || tracker?._id;
    if (!id || !file) return;
    try {
      setImportingRequirements(true);
      setImportResult(null);
      const text = await file.text();
      const res  = await axios.post(
        `${API_URL}/admin/cmmc/${id}/requirements/import`,
        text,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'text/plain' }
        }
      );
      if (res.data.ok) {
        setImportResult({
          updatedCount: res.data.updatedCount,
          skipped:      res.data.skipped?.length || 0,
          totalRows:    res.data.totalRows
        });
        await fetchTracker(id);
        await calculateScore(id);
      } else {
        setImportResult({ error: res.data.error || 'Import failed' });
      }
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || err.message || 'Import failed' });
    } finally {
      setImportingRequirements(false);
      if (requirementsFileRef.current) requirementsFileRef.current.value = '';
    }
  };

  const exportStepsCSV = async () => {
    const id = trackerId || tracker?._id;
    if (!id) return;
    try {
      const res = await fetch(`${API_URL}/admin/cmmc/${id}/steps/export`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `TJG_CMMC_Roadmap_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[CMMC] exportSteps error:', err);
    }
  };

  const importStepsCSV = async (file) => {
    const id = trackerId || tracker?._id;
    if (!id || !file) return;
    try {
      setImportingSteps(true);
      setStepsImportResult(null);
      const text = await file.text();
      const res  = await axios.post(
        `${API_URL}/admin/cmmc/${id}/steps/import`,
        text,
        { withCredentials: true, headers: { 'Content-Type': 'text/plain' } }
      );
      if (res.data.ok) {
        setStepsImportResult({
          updatedCount: res.data.updatedCount,
          skipped:      res.data.skipped?.length || 0,
          totalRows:    res.data.totalRows
        });
        await fetchTracker(id);
      } else {
        setStepsImportResult({ error: res.data.error || 'Import failed' });
      }
    } catch (err) {
      setStepsImportResult({ error: err.response?.data?.error || err.message || 'Import failed' });
    } finally {
      setImportingSteps(false);
      if (stepsFileRef.current) stepsFileRef.current.value = '';
    }
  };

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const reqs = tracker?.requirements || [];
  const steps = tracker?.steps || [];
  const clients = tracker?.clientEngagements || [];
  const assets = tracker?.assets || [];
  const policies = tracker?.policyDocuments || [];
  const questions = tracker?.leadershipQuestions || [];
 
  const reqSummary = {
    total: reqs.length, met: reqs.filter(r => r.status === 'met').length,
    notMet: reqs.filter(r => r.status === 'not_met').length, na: reqs.filter(r => r.status === 'not_applicable').length,
    notAssessed: reqs.filter(r => r.status === 'not_assessed').length, onPoam: reqs.filter(r => r.onPoam).length,
    criticalNotMet: reqs.filter(r => r.isCritical && r.status === 'not_met').length
  };
  const stepSummary = { total: steps.length, completed: steps.filter(s => s.status === 'completed').length };
  const overallPct = steps.length ? Math.round((stepSummary.completed / steps.length) * 100) : 0;
  const reqPct = reqs.length ? Math.round((reqSummary.met / reqs.length) * 100) : 0;
  const sprsScore = tracker?.calculatedSprsScore ?? tracker?.sprsScore?.current ?? null;
  const filteredReqs = activeFamily === 'all' ? reqs : reqs.filter(r => r.family === activeFamily);
  const unansweredQs = questions.filter(q => !q.answered).length;
 
  // ============================================================================
  // RENDER: INITIALIZATION SCREEN
  // ============================================================================
  if (!loading && !tracker) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.header}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: '#3B82F6', textTransform: 'uppercase' }}>CMMC Compliance Tracker</div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#F8FAFC', margin: '4px 0' }}>Initialize Compliance Tracker</h1>
            <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>This creates TJG's organizational CMMC tracker with all 110 NIST SP 800-171 R2 requirements, the DHRA engagement, and your in-scope systems pre-loaded.</p>
          </div>
          <div style={{ ...S.card, maxWidth: 520 }}>
            {[
              { key: 'orgName', label: 'Organization Name', placeholder: 'TJG' },
              { key: 'orgFullName', label: 'Full Legal Name', placeholder: 'The Joachim Group CPAs & Consultants, LLC' },
              { key: 'cageCode', label: 'CAGE Code', placeholder: 'e.g. 5ABC1' },
              { key: 'uei', label: 'UEI (SAM.gov)', placeholder: 'Unique Entity Identifier' }
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <div style={S.label}>{f.label}</div>
                <input style={S.input} value={initForm[f.key]} onChange={e => setInitForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} />
              </div>
            ))}
            <div style={{ marginBottom: 16 }}>
              <div style={S.label}>Target CMMC Level</div>
              <select style={S.select} value={initForm.targetLevel} onChange={e => setInitForm(p => ({ ...p, targetLevel: e.target.value }))}>
                <option value="level1">Level 1 (FCI Only)</option>
                <option value="level2_self">Level 2 Self-Assessment</option>
                <option value="level2_c3pao">Level 2 C3PAO Certification</option>
                <option value="level3">Level 3</option>
              </select>
            </div>
            <button style={S.btn('#10B981')} onClick={initializeTracker}>Initialize CMMC Tracker</button>
            {error && <div style={{ color: '#EF4444', marginTop: 8, fontSize: 13 }}>{error}</div>}
          </div>
        </div>
      </div>
    );
  }
 
  // ============================================================================
  // RENDER: MAIN DASHBOARD
  // ============================================================================
  return (
    <div style={S.page}>
      <div style={S.container}>
        {/* HEADER */}
        <div style={S.header}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: '#3B82F6', textTransform: 'uppercase' }}>CMMC Compliance Tracker</div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', margin: '4px 0' }}>{tracker?.orgName || 'CMMC Dashboard'}</h1>
              <div style={{ fontSize: 12, color: '#64748B' }}>
                {tracker?.cageCode && `CAGE: ${tracker.cageCode} | `}
                {tracker?.targetLevel?.replace(/_/g, ' ').toUpperCase()}
                {tracker?.assessmentStatus && ` | Status: ${tracker.assessmentStatus.replace(/_/g, ' ').toUpperCase()}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {unansweredQs > 0 && <span style={{ ...S.badge('#F59E0B'), fontSize: 11 }}>{unansweredQs} questions pending</span>}
              <button
                style={{ ...S.btn('#F59E0B'), opacity: calculatingScore ? 0.6 : 1 }}
                onClick={() => calculateScore()}
                disabled={calculatingScore}
              >{calculatingScore ? 'Calculating...' : 'Calculate Score'}</button>
              <button
                style={{ ...S.btnSm('#334155'), opacity: refreshing ? 0.6 : 1 }}
                disabled={refreshing}
                onClick={async () => {
                  const id = trackerId || tracker?._id;
                  if (!id) return;
                  setRefreshing(true);
                  await fetchTracker(id);
                  setRefreshing(false);
                }}
              >{refreshing ? '...' : 'Refresh'}</button>
            </div>
          </div>
        </div>
 
        {/* TABS */}
        <div style={S.tabs}>
          {[
            { key: 'dashboard', label: '📊 Dashboard' },
            { key: 'requirements', label: '📋 Requirements' },
            { key: 'roadmap', label: '🗺️ Roadmap' },
            { key: 'clients', label: '🏛️ Clients' },
            { key: 'assets', label: '💻 Assets' },
            { key: 'policies', label: '📜 Policies' },
            { key: 'questions', label: '❓ Questions' },
            { key: 'score', label: '🎯 SPRS Score' },
            { key: 'activity', label: '📜 Activity' }
          ].map(t => (
            <button key={t.key} style={S.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>{t.label}</button>
          ))}
        </div>
 
        {loading && <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>Loading CMMC data...</div>}
 
        {!loading && tracker && (
          <>
            {/* ============ DASHBOARD TAB ============ */}
            {activeTab === 'dashboard' && (
              <div>
                <div style={S.grid}>
                  <div style={S.statCard}>
                    <div style={S.label}>SPRS Score</div>
                    <div style={{ ...S.value, color: reqSummary.notAssessed > 0 ? '#F59E0B' : sprsScore === 110 ? '#10B981' : sprsScore >= 88 ? '#F59E0B' : '#EF4444' }}>
                      {reqSummary.notAssessed > 0 ? '—' : (sprsScore !== null ? sprsScore : '—')}<span style={{ fontSize: 12, color: '#64748B' }}>/110</span>
                    </div>
                    {reqSummary.notAssessed > 0 && <div style={{ fontSize: 10, color: '#F59E0B' }}>{reqSummary.notAssessed} not assessed</div>}
                  </div>
                  <div style={S.statCard}>
                    <div style={S.label}>Requirements Met</div>
                    <div style={S.value}>{reqSummary.met}<span style={{ fontSize: 12, color: '#64748B' }}>/{reqSummary.total}</span></div>
                    <ProgressBar pct={reqPct} color="#10B981" />
                  </div>
                  <div style={S.statCard}>
                    <div style={S.label}>Roadmap</div>
                    <div style={S.value}>{overallPct}%</div>
                    <ProgressBar pct={overallPct} color="#3B82F6" />
                  </div>
                  <div style={S.statCard}>
                    <div style={S.label}>Critical Gaps</div>
                    <div style={{ ...S.value, color: reqSummary.criticalNotMet > 0 ? '#EF4444' : '#10B981' }}>{reqSummary.criticalNotMet}</div>
                    <div style={{ fontSize: 10, color: '#64748B' }}>Cannot be on POA&M</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.label}>POA&M Items</div>
                    <div style={S.value}>{reqSummary.onPoam}</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.label}>Not Assessed</div>
                    <div style={{ ...S.value, color: '#475569' }}>{reqSummary.notAssessed}</div>
                  </div>
                </div>
 
                {/* Control Family Grid */}
                <div style={{ ...S.card, marginTop: 12 }}>
                  <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Requirements by Control Family</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                    {Object.entries(FAMILIES).map(([key, fam]) => {
                      const famReqs = reqs.filter(r => r.family === key);
                      const met = famReqs.filter(r => r.status === 'met').length;
                      const pct = famReqs.length ? Math.round((met / famReqs.length) * 100) : 0;
                      return (
                        <div key={key} style={{ background: '#1E293B', borderRadius: 8, padding: 10, cursor: 'pointer', border: `1px solid ${fam.color}30`, transition: 'border-color 0.15s' }}
                          onClick={() => { setActiveTab('requirements'); setActiveFamily(key); }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontWeight: 700, fontSize: 12, color: fam.color }}>{key}</span>
                            <span style={{ fontSize: 11, color: '#94A3B8' }}>{met}/{famReqs.length}</span>
                          </div>
                          <div style={{ fontSize: 11, color: '#CBD5E1', marginBottom: 6 }}>{fam.name}</div>
                          <ProgressBar pct={pct} color={fam.color} h={6} />
                        </div>
                      );
                    })}
                  </div>
                </div>
 
                {/* Phase Progress */}
                <div style={{ ...S.card, marginTop: 12 }}>
                  <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Roadmap Progress</h3>
                  {PHASES.map(phase => {
                    const ps = steps.filter(s => s.phaseId === phase.id);
                    const done = ps.filter(s => s.status === 'completed').length;
                    const pct = ps.length ? Math.round((done / ps.length) * 100) : 0;
                    return (
                      <div key={phase.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, cursor: 'pointer' }} onClick={() => setActiveTab('roadmap')}>
                        <span style={{ fontSize: 18, width: 28 }}>{phase.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{phase.name}</span>
                            <span style={{ fontSize: 11, color: '#64748B' }}>{done}/{ps.length}</span>
                          </div>
                          <ProgressBar pct={pct} color={phase.color} h={6} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
 
            {/* ============ REQUIREMENTS TAB ============ */}
            {activeTab === 'requirements' && (
              <div>
                {/* Toolbar: family filters + Export/Import buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <button style={S.famBtn(activeFamily === 'all', '#3B82F6')} onClick={() => setActiveFamily('all')}>All ({reqs.length})</button>
                    {Object.entries(FAMILIES).map(([key, fam]) => (
                      <button key={key} style={S.famBtn(activeFamily === key, fam.color)} onClick={() => setActiveFamily(key)}>
                        {key} ({reqs.filter(r => r.family === key).length})
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                    {importingRequirements && (
                      <span style={{ fontSize: 11, color: '#3B82F6', fontWeight: 600 }}>Importing...</span>
                    )}
                    <button
                      style={S.btn('#0EA5E9')}
                      onClick={exportRequirementsCSV}
                      title="Download all 110 requirements as CSV for offline editing"
                    >
                      ⬇ Export CSV
                    </button>
                    <label
                      style={{ ...S.btn('#10B981'), cursor: 'pointer', opacity: importingRequirements ? 0.6 : 1, display: 'inline-block' }}
                      title="Upload a completed CSV to bulk-update statuses"
                    >
                      ⬆ Import CSV
                      <input
                        ref={requirementsFileRef}
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        style={{ display: 'none' }}
                        disabled={importingRequirements}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) importRequirementsCSV(file);
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* Import result banner */}
                {importResult && !importingRequirements && (
                  <div style={{
                    background: importResult.error ? '#EF444415' : '#10B98115',
                    border: `1px solid ${importResult.error ? '#EF444440' : '#10B98140'}`,
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    {importResult.error ? (
                      <span style={{ fontSize: 12, color: '#FCA5A5', fontWeight: 600 }}>
                        Import failed: {importResult.error}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#10B981', fontWeight: 600 }}>
                        Import complete: {importResult.updatedCount} of {importResult.totalRows} requirements updated
                        {importResult.skipped > 0 ? ` (${importResult.skipped} skipped)` : ''}
                      </span>
                    )}
                    <button
                      style={{ ...S.btnSm('#334155'), marginLeft: 8 }}
                      onClick={() => setImportResult(null)}
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {filteredReqs.map(req => {
                  const isExp = expandedReqs.has(req.reqId);
                  const fam = FAMILIES[req.family] || {};
                  return (
                    <div key={req.reqId} style={{ ...S.card, borderLeft: `3px solid ${fam.color || '#3B82F6'}`, marginBottom: 6, padding: isExp ? 16 : 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                        onClick={() => setExpandedReqs(prev => { const n = new Set(prev); n.has(req.reqId) ? n.delete(req.reqId) : n.add(req.reqId); return n; })}>
                        <div style={{ width: 90, flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: fam.color }}>{req.reqId.split('-')[0]}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>
                            {req.title}
                            {req.isCritical && <span style={{ ...S.badge('#EF4444'), marginLeft: 6, fontSize: 9 }}>CRITICAL</span>}
                            {req.onPoam && <span style={{ ...S.badge('#F59E0B'), marginLeft: 6, fontSize: 9 }}>POA&M</span>}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>{req.reqId} | {req.isDerived ? 'Derived' : 'Basic'} | {req.pointValue}pts</div>
                        </div>
                        <select style={{ ...S.select, width: 130, fontSize: 12 }} value={req.status}
                          onClick={e => e.stopPropagation()} onChange={e => updateRequirement(req.reqId, { status: e.target.value })}>
                          <option value="not_assessed">Not Assessed</option>
                          <option value="met">MET</option>
                          <option value="not_met">NOT MET</option>
                          <option value="not_applicable">N/A</option>
                        </select>
                        <span style={S.badge(STATUS_COLORS[req.status])}>{savingReq === req.reqId ? '...' : STATUS_LABELS[req.status]}</span>
                        <span style={{ color: '#64748B', fontSize: 14 }}>{isExp ? '▲' : '▼'}</span>
                      </div>
                      {isExp && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1E293B' }}>
                          <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 10 }}>{req.description}</div>
                          <div style={{ marginBottom: 8 }}>
                            <div style={S.label}>Implementation Description</div>
                            <textarea style={S.textarea} value={req.implementationDesc || ''} placeholder="How does TJG implement this requirement?"
                              onBlur={e => updateRequirement(req.reqId, { implementationDesc: e.target.value })}
                              onChange={e => { setTracker(prev => { if (!prev) return prev; const rs = [...prev.requirements]; const idx = rs.findIndex(r => r.reqId === req.reqId); if (idx !== -1) rs[idx] = { ...rs[idx], implementationDesc: e.target.value }; return { ...prev, requirements: rs }; }); }} />
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <div style={S.label}>Notes</div>
                            <textarea style={{ ...S.textarea, minHeight: 40 }} value={req.notes || ''} placeholder="Add notes..."
                              onBlur={e => updateRequirement(req.reqId, { notes: e.target.value })}
                              onChange={e => { setTracker(prev => { if (!prev) return prev; const rs = [...prev.requirements]; const idx = rs.findIndex(r => r.reqId === req.reqId); if (idx !== -1) rs[idx] = { ...rs[idx], notes: e.target.value }; return { ...prev, requirements: rs }; }); }} />
                          </div>
                          {req.status === 'not_met' && !req.isCritical && (
                            <div style={{ background: '#F59E0B10', border: '1px solid #F59E0B30', borderRadius: 8, padding: 10, marginTop: 8 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                <input type="checkbox" checked={req.onPoam || false} onChange={e => updateRequirement(req.reqId, { onPoam: e.target.checked })} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#F59E0B' }}>Add to POA&M</span>
                              </label>
                            </div>
                          )}
                          {req.evidenceFiles?.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <div style={S.label}>Evidence Files ({req.evidenceFiles.length})</div>
                              {req.evidenceFiles.map((f, i) => (
                                <div key={i} style={{ fontSize: 11, color: '#94A3B8', padding: '2px 0' }}>📎 {f.originalName} ({(f.size / 1024).toFixed(1)} KB)</div>
                              ))}
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: '#475569', marginTop: 8 }}>Last assessed: {fmtDate(req.lastAssessedAt)}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
 
            {/* ============ ROADMAP TAB ============ */}
            {activeTab === 'roadmap' && (
              <div>
                {/* Toolbar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  {importingSteps && (
                    <span style={{ fontSize: 11, color: '#3B82F6', fontWeight: 600 }}>Importing...</span>
                  )}
                  <button
                    style={S.btn('#0EA5E9')}
                    onClick={exportStepsCSV}
                    title="Download all roadmap steps as CSV for offline editing"
                  >
                    ⬇ Export CSV
                  </button>
                  <label
                    style={{ ...S.btn('#10B981'), cursor: 'pointer', opacity: importingSteps ? 0.6 : 1, display: 'inline-block' }}
                    title="Upload a completed CSV to bulk-update step statuses"
                  >
                    ⬆ Import CSV
                    <input
                      ref={stepsFileRef}
                      type="file"
                      accept=".csv,text/csv,text/plain"
                      style={{ display: 'none' }}
                      disabled={importingSteps}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) importStepsCSV(file);
                      }}
                    />
                  </label>
                </div>

                {/* Import result banner */}
                {stepsImportResult && !importingSteps && (
                  <div style={{
                    background: stepsImportResult.error ? '#EF444415' : '#10B98115',
                    border: `1px solid ${stepsImportResult.error ? '#EF444440' : '#10B98140'}`,
                    borderRadius: 8, padding: '10px 14px', marginBottom: 12,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    {stepsImportResult.error ? (
                      <span style={{ fontSize: 12, color: '#FCA5A5', fontWeight: 600 }}>
                        Import failed: {stepsImportResult.error}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#10B981', fontWeight: 600 }}>
                        Import complete: {stepsImportResult.updatedCount} of {stepsImportResult.totalRows} steps updated
                        {stepsImportResult.skipped > 0 ? ` (${stepsImportResult.skipped} skipped)` : ''}
                      </span>
                    )}
                    <button
                      style={{ ...S.btnSm('#334155'), marginLeft: 8 }}
                      onClick={() => setStepsImportResult(null)}
                    >Dismiss</button>
                  </div>
                )}

                {PHASES.map(phase => {
                  const ps = steps.filter(s => s.phaseId === phase.id);
                  const done = ps.filter(s => s.status === 'completed').length;
                  return (
                    <div key={phase.id} style={{ ...S.card, marginBottom: 16, borderLeft: `3px solid ${phase.color}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div><span style={{ fontSize: 20, marginRight: 8 }}>{phase.icon}</span><span style={{ fontSize: 16, fontWeight: 700, color: phase.color }}>{phase.name}</span></div>
                        <span style={{ fontSize: 12, color: '#64748B' }}>{done}/{ps.length}</span>
                      </div>
                      {ps.map(step => (
                        <div key={step.stepId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #1E293B' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800,
                            background: `${STATUS_COLORS[step.status]}15`, color: STATUS_COLORS[step.status], border: `1px solid ${STATUS_COLORS[step.status]}40` }}>
                            {step.status === 'completed' ? '✓' : step.stepId}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{step.title}</div>
                            {step.completedAt && <div style={{ fontSize: 10, color: '#64748B' }}>Completed: {fmtDate(step.completedAt)}</div>}
                          </div>
                          <select style={{ ...S.select, width: 130, fontSize: 12 }} value={step.status} onChange={e => updateStep(step.stepId, { status: e.target.value })}>
                            <option value="not_started">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="blocked">Blocked</option>
                          </select>
                          <span style={S.badge(STATUS_COLORS[step.status])}>{STATUS_LABELS[step.status]}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
 
            {/* ============ CLIENTS TAB ============ */}
            {activeTab === 'clients' && (
              <div>
                <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Client Engagements ({clients.length})</h3>
                {clients.map((c, i) => (
                  <div key={c._id || i} style={{ ...S.card, borderLeft: `3px solid ${c.clientType === 'dod' ? '#3B82F6' : c.clientType === 'federal_civilian' ? '#8B5CF6' : '#10B981'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#F8FAFC' }}>{c.clientName}</div>
                        <div style={{ fontSize: 12, color: '#64748B' }}>{c.clientFullName}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={S.badge(c.clientType === 'dod' ? '#3B82F6' : '#10B981')}>{c.clientType?.toUpperCase()}</span>
                        <span style={S.badge(c.accessEnvironment?.type === 'hybrid' ? '#F59E0B' : c.accessEnvironment?.type === 'gfe_only' ? '#10B981' : '#EF4444')}>
                          {c.accessEnvironment?.type?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {c.accessEnvironment?.description && <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>{c.accessEnvironment.description}</div>}
                    {c.scopeImpact?.bringsDataIntoTjgSystems && (
                      <div style={{ background: '#EF444415', border: '1px solid #EF444440', borderRadius: 6, padding: 8, fontSize: 11, color: '#FCA5A5', marginBottom: 8 }}>
                        CUI enters TJG systems. TJG's contractor-owned environment is IN SCOPE.
                      </div>
                    )}
                    {c.dataFlows?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={S.label}>Data Flows ({c.dataFlows.length})</div>
                        {c.dataFlows.map((df, j) => (
                          <div key={j} style={{ fontSize: 11, color: '#94A3B8', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={S.badge(df.isInScope ? '#EF4444' : '#10B981')}>{df.isInScope ? 'IN SCOPE' : 'OUT'}</span>
                            {df.source} → {df.destination}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {clients.length === 0 && <div style={{ color: '#64748B', padding: 20, textAlign: 'center' }}>No client engagements yet.</div>}
              </div>
            )}
 
            {/* ============ ASSETS TAB ============ */}
            {activeTab === 'assets' && (
              <div>
                <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Asset Inventory ({assets.length})</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
                  {assets.map((a, i) => (
                    <div key={a._id || i} style={{ ...S.card, borderLeft: `3px solid ${a.isInScope ? '#3B82F6' : '#475569'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#F8FAFC' }}>{a.name}</div>
                        <span style={S.badge(a.isInScope ? '#3B82F6' : '#475569')}>{a.isInScope ? 'IN SCOPE' : 'OUT'}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>{a.type} | {a.category?.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>{a.description?.substring(0, 120)}{a.description?.length > 120 ? '...' : ''}</div>
                      {a.notes && <div style={{ fontSize: 10, color: '#F59E0B', marginTop: 6 }}>📌 {a.notes.substring(0, 100)}</div>}
                    </div>
                  ))}
                </div>
                {assets.length === 0 && <div style={{ color: '#64748B', padding: 20, textAlign: 'center' }}>No assets in inventory.</div>}
              </div>
            )}
 
            {/* ============ POLICIES TAB ============ */}
            {activeTab === 'policies' && (
              <div>
                <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Policy Documents (14 Families)</h3>
                {policies.map((p, i) => {
                  const pct = { not_started: 0, draft: 25, review: 50, approved: 75, published: 100 }[p.status] || 0;
                  const fam = FAMILIES[p.familyCode] || {};
                  return (
                    <div key={p.familyCode || i} style={{ ...S.card, marginBottom: 8, borderLeft: `3px solid ${fam.color || '#64748B'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0' }}>{p.familyCode} - {p.familyName}</div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>{p.policyTitle || 'Not started'}</div>
                        </div>
                        <span style={S.badge(pct === 100 ? '#10B981' : pct > 0 ? '#F59E0B' : '#475569')}>
                          {p.status?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>
                      <ProgressBar pct={pct} color={fam.color || '#3B82F6'} h={4} />
                    </div>
                  );
                })}
              </div>
            )}
 
            {/* ============ QUESTIONS TAB ============ */}
            {activeTab === 'questions' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ color: '#F8FAFC', margin: '0 0 4px 0', fontSize: 15 }}>Open Items</h3>
                    <p style={{ color: '#64748B', fontSize: 12, margin: 0 }}>
                      Information needed to complete the assessment. Gather answers and enter them below — each answer auto-updates the related NIST controls.
                      {questions.length > 0 && ` ${unansweredQs} of ${questions.length} remaining.`}
                      {questions.filter(q => q.pinned).length > 0 && ` 📌 ${questions.filter(q => q.pinned).length} pinned (won't be replaced on regenerate).`}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {questions.length > 0 && (
                      <button
                        style={{ ...S.btn('#0EA5E9'), whiteSpace: 'nowrap' }}
                        onClick={exportQuestionsCSV}
                        title="Download all questions and answers as CSV"
                      >⬇ Export CSV</button>
                    )}
                    <button
                      style={{ ...S.btn('#8B5CF6'), opacity: generatingQuestions ? 0.6 : 1, whiteSpace: 'nowrap' }}
                      onClick={generateQuestions}
                      disabled={generatingQuestions}
                    >
                      {generatingQuestions ? '⏳ Analyzing gaps...' : questions.length > 0 ? '🔄 Regenerate' : '✨ Generate Open Items'}
                    </button>
                  </div>
                </div>

                {generationSummary && !generatingQuestions && (
                  <div style={{ background: '#8B5CF615', border: '1px solid #8B5CF640', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#8B5CF6', marginBottom: 4 }}>
                      ✅ {generationSummary.count} open items generated{generationSummary.preserved > 0 ? ` · ${generationSummary.preserved} existing answers preserved` : ''}{generationSummary.pinned > 0 ? ` · ${generationSummary.pinned} pinned` : ''}
                    </div>
                    {generationSummary.coverage && (
                      <div style={{ fontSize: 11, color: generationSummary.coverage.coveragePct === 100 ? '#10B981' : '#F59E0B', fontWeight: 600, marginBottom: 4 }}>
                        Coverage: {generationSummary.coverage.covered}/{generationSummary.coverage.totalRequirements} requirements ({generationSummary.coverage.coveragePct}%)
                        {generationSummary.coverage.fallbacksAdded > 0 ? ` · ${generationSummary.coverage.fallbacksAdded} fallback question(s) auto-added for full coverage` : ''}
                      </div>
                    )}
                    {generationSummary.summary && <div style={{ fontSize: 11, color: '#94A3B8' }}>{generationSummary.summary}</div>}
                  </div>
                )}

                {questions.length === 0 && !generatingQuestions && (
                  <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>✨</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F0', marginBottom: 8 }}>No open items yet</div>
                    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>
                      Click "Generate Open Items" to have Azure OpenAI analyze the current state of all 110 NIST requirements and produce a focused list of information you need to gather.
                    </div>
                    <button style={S.btn('#8B5CF6')} onClick={generateQuestions}>✨ Generate Open Items</button>
                  </div>
                )}

                {generatingQuestions && (
                  <div style={{ ...S.card, padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#8B5CF6', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#8B5CF6' }}>Azure OpenAI is analyzing your compliance gaps...</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 32 }}>
                      <div style={{ fontSize: 11, color: '#64748B' }}>① Reading all 110 NIST SP 800-171 R2 requirement statuses</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>② Identifying not-assessed and not-met controls</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>③ Grouping related controls thematically</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>④ Generating focused open items for information gathering</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>⑤ Preserving any existing answered items</div>
                    </div>
                  </div>
                )}

                {processingQuestion && (
                  <div style={{ background: '#1E293B', border: '1px solid #3B82F6', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ position: 'relative', width: 18, height: 18, flexShrink: 0 }}>
                      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #3B82F630' }} />
                      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#3B82F6', animation: 'spin 0.8s linear infinite' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#3B82F6' }}>Azure OpenAI is analyzing your answer...</div>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 1 }}>Classifying intent and mapping to NIST SP 800-171 requirements</div>
                    </div>
                  </div>
                )}

                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

                {questions.map((q, i) => {
                  const isEditing    = editingQuestion === q.id;
                  const isProcessing = processingQuestion === q.id;
                  return (
                    <div key={q.id || i} style={{ ...S.card, borderLeft: `3px solid ${isProcessing ? '#3B82F6' : q.pinned ? '#F59E0B' : q.answered && !isEditing ? '#10B981' : q.impact === 'critical' ? '#EF4444' : '#F59E0B'}`, marginBottom: 8, opacity: processingQuestion && !isProcessing ? 0.5 : 1, transition: 'opacity 0.2s, border-color 0.2s' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#E2E8F0', paddingRight: 8 }}>
                          {q.pinned && <span style={{ fontSize: 11, marginRight: 6 }}>📌</span>}
                          {q.question}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                          {isProcessing && <span style={{ fontSize: 10, color: '#3B82F6', fontWeight: 600 }}>🤖 AI analyzing...</span>}
                          <button
                            title={q.pinned ? 'Unpin — allow regeneration to replace this question' : 'Pin — keep this question even after regeneration'}
                            style={{ padding: '3px 7px', background: q.pinned ? '#F59E0B20' : '#1E293B', border: `1px solid ${q.pinned ? '#F59E0B60' : '#334155'}`, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: q.pinned ? '#F59E0B' : '#64748B', fontWeight: 600 }}
                            onClick={() => pinQuestion(q.id, !q.pinned)}
                          >{q.pinned ? '📌' : '📍'}</button>
                          <span style={S.badge(q.impact === 'critical' ? '#EF4444' : '#F59E0B')}>{q.impact?.toUpperCase()}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 8 }}>{q.why}</div>

                      {answerResults[q.id] && !isProcessing && (
                        <div style={{ fontSize: 10, color: answerResults[q.id].derivedCount > 0 ? '#10B981' : '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                          {answerResults[q.id].derivedCount > 0
                            ? `✓ ${answerResults[q.id].derivedCount} requirement(s) updated from this answer`
                            : '⚠ No requirements were updated — answer may need more specific detail'}
                        </div>
                      )}

                      {isProcessing && (
                        <div style={{ background: '#0F172A', border: '1px solid #3B82F640', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#3B82F6', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                            <span style={{ fontSize: 11, color: '#3B82F6', fontWeight: 600 }}>Processing with Azure OpenAI (TJG_RPA_GPT4)</span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 22 }}>
                            <div style={{ fontSize: 10, color: '#64748B' }}>① Classifying intent from your answer</div>
                            <div style={{ fontSize: 10, color: '#64748B' }}>② Mapping to applicable NIST SP 800-171 R2 controls</div>
                            <div style={{ fontSize: 10, color: '#64748B' }}>③ Updating requirement statuses and recalculating SPRS score</div>
                          </div>
                        </div>
                      )}

                      {q.answered && !isEditing && !isProcessing ? (
                        <div style={{ background: '#10B98115', border: '1px solid #10B98140', borderRadius: 6, padding: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontSize: 12, color: '#10B981', fontWeight: 600 }}>✅ Answered: {q.answer}</div>
                              <div style={{ fontSize: 10, color: '#64748B' }}>by {q.answeredBy} on {fmtDate(q.answeredAt)}</div>
                              {q.id && (RESERVED_QUESTION_IDS[q.id] || (q.controlsAffected && q.controlsAffected.length > 0)) && (
                                <div style={{ fontSize: 10, color: '#3B82F6', marginTop: 3 }}>
                                  ⚡ This answer automatically updates related NIST controls and SPRS score
                                </div>
                              )}
                              {q.pinned && (
                                <div style={{ fontSize: 10, color: '#F59E0B', marginTop: 3 }}>
                                  📌 Pinned — this question will be preserved on regeneration
                                </div>
                              )}
                            </div>
                            <button style={{ ...S.btnSm('#334155'), marginLeft: 8 }} onClick={() => { setEditingQuestion(q.id); setQuestionDrafts(prev => ({ ...prev, [q.id]: q.answer || '' })); }}>Edit</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            style={{ ...S.input, flex: 1 }}
                            value={questionDrafts[q.id] || ''}
                            placeholder="Enter answer..."
                            onChange={e => setQuestionDrafts(prev => ({ ...prev, [q.id]: e.target.value }))}
                            onKeyDown={e => {
                              const val = (questionDrafts[q.id] || '').trim();
                              if (e.key === 'Enter' && val) answerQuestion(q.id, val);
                            }}
                            autoFocus={isEditing}
                          />
                          <button style={S.btnSm('#10B981')} onClick={() => {
                            const val = (questionDrafts[q.id] || '').trim();
                            if (val) answerQuestion(q.id, val);
                          }}>Save</button>
                          {isEditing && <button style={S.btnSm('#EF4444')} onClick={() => {
                            setEditingQuestion(null);
                            setQuestionDrafts(prev => { const next = { ...prev }; delete next[q.id]; return next; });
                          }}>Cancel</button>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
 
            {/* ============ SCORE TAB ============ */}
            {activeTab === 'score' && (
              <div>
                <div style={S.grid}>
                  <div style={{ ...S.card, flex: '1 1 260px', textAlign: 'center', padding: 24, borderTop: '3px solid #3B82F6' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#3B82F6', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Progress Score</div>
                    <div style={{ fontSize: 52, fontWeight: 900, color: scoreResult ? (scoreResult.progressPercentage === 100 ? '#10B981' : scoreResult.progressPercentage >= 80 ? '#F59E0B' : '#EF4444') : '#64748B' }}>
                      {scoreResult ? scoreResult.progressScore : '—'}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748B' }}>of {scoreResult ? scoreResult.progressMaxPossible : '?'} assessed pts</div>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                      Reflects {scoreResult ? (scoreResult.summary.total - scoreResult.summary.notAssessed) : 0} assessed requirements only.
                      Moves as you answer leadership questions and update requirements.
                    </div>
                  </div>
                  <div style={{ ...S.card, flex: '1 1 260px', textAlign: 'center', padding: 24, borderTop: '3px solid #475569' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>SPRS Submission Score</div>
                    <div style={{ fontSize: 52, fontWeight: 900, color: reqSummary.notAssessed > 0 ? '#475569' : (sprsScore === 110 ? '#10B981' : sprsScore !== null && sprsScore >= 88 ? '#F59E0B' : '#EF4444') }}>
                      {reqSummary.notAssessed > 0 ? '—' : (sprsScore !== null ? sprsScore : '—')}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748B' }}>out of 110</div>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                      Official SPRS score. Locked until all {reqSummary.total} requirements are assessed.
                    </div>
                  </div>
                </div>
                {reqSummary.notAssessed > 0 && (
                  <div style={{ background: '#F59E0B15', border: '1px solid #F59E0B40', borderRadius: 8, padding: 12, margin: '12px 0', textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, color: '#F59E0B', fontSize: 13, marginBottom: 4 }}>
                      Assessment Incomplete — {reqSummary.notAssessed} of {reqSummary.total} requirements not yet assessed
                    </div>
                    <div style={{ fontSize: 12, color: '#CBD5E1' }}>
                      SPRS submission score is locked until every requirement is evaluated as MET, NOT MET, or N/A.
                      Watch the Progress Score as you answer leadership questions and work through the requirements tab.
                    </div>
                  </div>
                )}
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <button style={{ ...S.btn('#3B82F6'), opacity: calculatingScore ? 0.6 : 1 }} onClick={calculateScore} disabled={calculatingScore}>
                    {calculatingScore ? 'Calculating...' : 'Recalculate'}
                  </button>
                </div>
                {scoreResult && (
                  <div style={{ ...S.card, marginTop: 12 }}>
                    <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Score Breakdown</h3>
                    <div style={S.grid}>
                      <div style={S.statCard}>
                        <div style={S.label}>Status</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: scoreResult.status === 'FINAL' ? '#10B981' : scoreResult.status === 'CONDITIONAL' ? '#F59E0B' : '#EF4444' }}>{scoreResult.status}</div>
                      </div>
                      <div style={S.statCard}>
                        <div style={S.label}>Submission Score</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#64748B' }}>{scoreResult.score !== null ? `${scoreResult.score}/110 (${scoreResult.percentage}%)` : '— (incomplete)'}</div>
                      </div>
                      <div style={S.statCard}>
                        <div style={S.label}>POA&M Eligible</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: scoreResult.assessmentComplete ? (scoreResult.poamEligible ? '#10B981' : '#EF4444') : '#64748B' }}>{scoreResult.assessmentComplete ? (scoreResult.poamEligible ? 'YES' : 'NO') : '—'}</div>
                      </div>
                    </div>
                    {scoreResult.summary && (
                      <div style={{ ...S.grid, marginTop: 12 }}>
                        <div style={S.statCard}>
                          <div style={S.label}>Met</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#10B981' }}>{scoreResult.summary.met}</div>
                        </div>
                        <div style={S.statCard}>
                          <div style={S.label}>Not Met</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#EF4444' }}>{scoreResult.summary.notMet}</div>
                        </div>
                        <div style={S.statCard}>
                          <div style={S.label}>N/A</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#6B7280' }}>{scoreResult.summary.notApplicable}</div>
                        </div>
                        <div style={S.statCard}>
                          <div style={S.label}>Not Assessed</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#F59E0B' }}>{scoreResult.summary.notAssessed}</div>
                        </div>
                      </div>
                    )}
                    {scoreResult.criticalNotMet?.length > 0 && (
                      <div style={{ background: '#EF444415', border: '1px solid #EF444440', borderRadius: 8, padding: 12, marginTop: 12 }}>
                        <div style={{ fontWeight: 700, color: '#EF4444', fontSize: 13, marginBottom: 8 }}>Critical Requirements NOT MET ({scoreResult.criticalNotMet.length})</div>
                        {scoreResult.criticalNotMet.map(r => (
                          <div key={r.reqId} style={{ fontSize: 12, color: '#FCA5A5', padding: '2px 0' }}>{r.reqId}: {r.title}</div>
                        ))}
                      </div>
                    )}
                    {(scoreResult.allDeductions || scoreResult.deductions)?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 700, color: '#F59E0B', fontSize: 13, marginBottom: 8 }}>Deductions ({scoreResult.deductions.length})</div>
                        {(scoreResult.allDeductions || scoreResult.deductions).slice(0, 20).map(d => (
                          <div key={d.reqId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#CBD5E1', padding: '3px 0', borderBottom: '1px solid #1E293B' }}>
                            <span>{d.reqId}: {d.title} {d.onPoam && '(POA&M)'} {d.reason === 'not_assessed' && <span style={{ color: '#F59E0B' }}>(not assessed)</span>}</span>
                            <span style={{ color: '#EF4444', fontWeight: 700 }}>{d.points}</span>
                          </div>
                        ))}
                        {(scoreResult.allDeductions || scoreResult.deductions).length > 20 && (
                          <div style={{ fontSize: 12, color: '#64748B', padding: '6px 0' }}>+{(scoreResult.allDeductions || scoreResult.deductions).length - 20} more deductions...</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
 
            {/* ============ ACTIVITY TAB ============ */}
            {activeTab === 'activity' && (
              <div style={S.card}>
                <h3 style={{ color: '#F8FAFC', margin: '0 0 12px 0', fontSize: 15 }}>Activity Log</h3>
                {(tracker.activityLog || []).slice().reverse().slice(0, 50).map((entry, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #1E293B', fontSize: 12 }}>
                    <div style={{ color: '#64748B', width: 100, flexShrink: 0 }}>{fmtDate(entry.timestamp)}</div>
                    <div style={{ color: '#3B82F6', width: 120, flexShrink: 0, fontWeight: 600 }}>{entry.adminName}</div>
                    <div style={{ color: '#CBD5E1', flex: 1 }}>{entry.details || entry.action}</div>
                  </div>
                ))}
                {(!tracker.activityLog || tracker.activityLog.length === 0) && (
                  <div style={{ color: '#64748B', padding: 20, textAlign: 'center' }}>No activity yet.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
 
export default CMMCDashboard;